import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Heart, Award, Users, TrendingUp, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const About = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success('Thank you for your message! We\'ll be in touch soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, setting the highest standards for our services and outcomes.'
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'We operate with complete transparency and honesty, building trust through every interaction.'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'We believe in the power of community and collective growth, supporting each other\'s success.'
    },
    {
      icon: TrendingUp,
      title: 'Growth',
      description: 'We are committed to continuous improvement and helping our clients achieve sustainable growth.'
    }
  ];

  const achievements = [
    'Helped 400+ families achieve financial independence',
    'Conducted 100+ seminars and workshops',
    'Developed 50+ successful business leaders',
    'Maintained 99% client satisfaction rate',
    '4+ years of consistent excellence',
    'Expanded to multiple cities nationwide'
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-6">About Us</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-8">
                Empowering <span className="text-gold-gradient">Families</span> to Build Lasting Wealth
              </h1>
              <p className="font-body text-white/70 text-base sm:text-lg leading-relaxed mb-8">
                Founded with a vision to democratize wealth-building knowledge, Empower Wealth Builders has grown into a trusted partner for hundreds of families seeking financial freedom and leadership excellence.
              </p>
              <p className="font-body text-white/50 text-base leading-relaxed">
                Our team of experienced financial advisors and leadership coaches work tirelessly to provide personalized guidance, transformative education, and ongoing support to help our clients achieve their dreams.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_wealth-builders-6/artifacts/nqsaelkj_IMG-20251020-WA0038.jpg"
                alt="Team Meeting"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gold-500 p-6 lg:p-8">
                <div className="font-heading text-4xl lg:text-5xl text-black">4+</div>
                <div className="font-body text-black/70 text-sm uppercase tracking-wider">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
            <div className="p-8 lg:p-12 border border-white/5 hover:border-gold-500/30 transition-colors duration-500">
              <div className="w-16 h-16 bg-gold-500/10 flex items-center justify-center mb-8">
                <Target className="w-8 h-8 text-gold-500" />
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl text-white mb-6">Our Mission</h3>
              <p className="font-body text-white/60 leading-relaxed">
                To empower individuals and families with the knowledge, skills, and mindset needed to build generational wealth and become influential leaders in their communities. We believe everyone deserves access to financial education and the opportunity to create lasting prosperity.
              </p>
            </div>
            <div className="p-8 lg:p-12 border border-white/5 hover:border-gold-500/30 transition-colors duration-500">
              <div className="w-16 h-16 bg-gold-500/10 flex items-center justify-center mb-8">
                <Eye className="w-8 h-8 text-gold-500" />
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl text-white mb-6">Our Vision</h3>
              <p className="font-body text-white/60 leading-relaxed">
                To create a world where financial literacy is universal, where every family has the tools to build wealth, and where leadership excellence is accessible to all who seek it. We envision communities transformed by empowered individuals making positive impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">Our Foundation</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Core <span className="text-gold-gradient">Values</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="group p-8 bg-white/[0.02] border border-white/5 hover:border-gold-500/30 transition-all duration-500"
              >
                <value.icon className="w-10 h-10 text-gold-500 mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h4 className="font-heading text-xl text-white mb-4">{value.title}</h4>
                <p className="font-body text-white/50 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">Our Journey</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Milestones & <span className="text-gold-gradient">Achievements</span>
              </h2>
              <p className="font-body text-white/60 text-base sm:text-lg leading-relaxed mb-8">
                Over the years, we've celebrated countless successes alongside our clients and partners. Here are some highlights of our journey.
              </p>
              <div className="space-y-4">
                {achievements.map((achievement, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <CheckCircle className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" />
                    <span className="font-body text-white/70">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-8 bg-white/[0.02] border border-white/5 text-center">
                <Award className="w-8 h-8 text-gold-500 mx-auto mb-4" />
                <div className="font-heading text-4xl text-gold-500 mb-2">400+</div>
                <div className="font-body text-white/50 text-sm uppercase tracking-wider">Families Empowered</div>
              </div>
              <div className="p-8 bg-white/[0.02] border border-white/5 text-center">
                <Users className="w-8 h-8 text-gold-500 mx-auto mb-4" />
                <div className="font-heading text-4xl text-gold-500 mb-2">99%</div>
                <div className="font-body text-white/50 text-sm uppercase tracking-wider">Satisfaction Rate</div>
              </div>
              <div className="p-8 bg-white/[0.02] border border-white/5 text-center col-span-2">
                <TrendingUp className="w-8 h-8 text-gold-500 mx-auto mb-4" />
                <div className="font-heading text-4xl text-gold-500 mb-2">4+</div>
                <div className="font-body text-white/50 text-sm uppercase tracking-wider">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">Get In Touch</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Let's <span className="text-gold-gradient">Connect</span>
              </h2>
              <p className="font-body text-white/60 text-base sm:text-lg leading-relaxed mb-8">
                Have questions about our services or want to learn more about how we can help you achieve your goals? We'd love to hear from you.
              </p>
              <div className="space-y-6">
                <div>
                  <h4 className="font-heading text-lg text-white mb-2">Address</h4>
                  <p className="font-body text-white/50">100 Milverton Drive, Mississauga, L5R 4H1, Ontario</p>
                </div>
                <div>
                  <h4 className="font-heading text-lg text-white mb-2">Email</h4>
                  <a href="mailto:Info@empowerwealthbuilder.com" className="font-body text-gold-500 hover:text-gold-400 transition-colors">
                    Info@empowerwealthbuilder.com
                  </a>
                </div>
                <div>
                  <h4 className="font-heading text-lg text-white mb-2">Phone</h4>
                  <a href="tel:+14379957262" className="font-body text-gold-500 hover:text-gold-400 transition-colors">
                    437-995-7262
                  </a>
                </div>
              </div>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div>
                <label className="block font-body text-sm text-white/70 mb-2">Full Name *</label>
                <input
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  data-testid="contact-name-input"
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50 transition-colors"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-white/70 mb-2">Email Address *</label>
                <input
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  data-testid="contact-email-input"
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50 transition-colors"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-white/70 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  data-testid="contact-phone-input"
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50 transition-colors"
                  placeholder="+1 (234) 567-890"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-white/70 mb-2">Message *</label>
                <textarea
                  required
                  rows={5}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  data-testid="contact-message-input"
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50 transition-colors resize-none"
                  placeholder="Tell us how we can help you..."
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="w-full bg-gold-500 text-black py-4 font-body font-semibold uppercase tracking-widest hover:bg-gold-400 transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
